import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Linkedin, MessageCircle, Send, ArrowUpRight, CheckCircle2, AlertCircle, LoaderCircle } from "lucide-react";
import { getApiBase } from "../services/apiBase";
import styles from "./Contacts.module.css";

const contactMethods = [
  { icon: Phone, label: "Mobile", value: "+64 21 085 73484", href: "tel:+642108573484" },
  { icon: Mail, label: "Email", value: "jipingcui0908@gmail.com", href: "mailto:jipingcui0908@gmail.com" },
  { icon: Linkedin, label: "LinkedIn", value: "Connect with me", href: "https://www.linkedin.com/in/kevinchoi0720/", external: true },
  { icon: MessageCircle, label: "WeChat", value: "canlight720" },
];
const container = { hidden: {}, visible: { transition: { staggerChildren: 0.09, delayChildren: 0.06 } } };
const rise = { hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0, transition: { duration: 0.48, ease: [0.16, 1, 0.3, 1] } } };

export default function Contacts() {
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setIsSubmitting(true); setStatus({ type: "idle", message: "" });
    try {
      const response = await fetch(`${getApiBase()}/api/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: form.name.value.trim(), email: form.email.value.trim(), message: form.message.value.trim() }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Unable to send your message.");
      form.reset(); setStatus({ type: "success", message: "Message sent - thank you for reaching out." });
    } catch (error) {
      setStatus({ type: "error", message: error.message || "Network error. Please try again." });
    } finally { setIsSubmitting(false); }
  };

  return <motion.main className={styles.page} variants={container} initial="hidden" animate="visible">
    <motion.header className={styles.intro} variants={rise}>
      <span className={styles.eyebrow}>Get in touch</span><h1>Let&apos;s create something meaningful.</h1>
    </motion.header>
    <div className={styles.grid}>
      <motion.section className={styles.contactPanel} variants={rise} aria-label="Contact methods">
        <div className={styles.panelHeading}>
          <span className={styles.headingMarker} />
          <h2>Contact details</h2>
        </div>
        <div className={styles.contactList}>{contactMethods.map(({ icon: Icon, label, value, href, external }, index) => {
          const body = <><span className={styles.contactIcon}><Icon size={18} strokeWidth={1.8} /></span>
            <span className={styles.contactCopy}>
              <strong>{label}</strong>
              <span>{value}</span>
            </span>
            {href && <ArrowUpRight className={styles.linkArrow} size={16} />}
          </>;
          return href ? <a key={label} className={styles.contactCard} href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} style={{ "--card-delay": `${index * 60}ms` }}>{body}</a> : <div key={label} className={styles.contactCard} style={{ "--card-delay": `${index * 60}ms` }}>{body}</div>;
        })}
        </div>
      </motion.section>
      <motion.section className={styles.formPanel} variants={rise}>
        <div className={styles.formHeading}>
          <span className={styles.formIcon}><Send size={18} /></span>
          <h2>Send a message</h2>
        </div>
        <form className={styles.form} onSubmit={handleSubmit} aria-busy={isSubmitting}>
          <div className={styles.fieldRow}>
            <label>
              <span>Your name</span>
              <input name="name" type="text" placeholder="Jane Smith" autoComplete="name" required />
            </label>
            <label>
              <span>Email address</span>
              <input name="email" type="email" placeholder="jane@example.com" autoComplete="email" required />
            </label>
          </div>
          <label>
            <span>Message</span>
            <textarea name="message" placeholder="Tell me a little about your idea..." rows="6" required />
          </label>
          {status.message && <div className={`${styles.status} ${status.type === "success" ? styles.success : styles.error}`} role="status">{status.type === "success" ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}<span>{status.message}</span></div>}
          <button className={styles.submit} type="submit" disabled={isSubmitting}>{isSubmitting ? <LoaderCircle className={styles.spinner} size={18} /> : <Send size={17} />}<span>{isSubmitting ? "Sending message..." : "Send message"}</span></button>
        </form>
      </motion.section>
    </div>
    <motion.p className={styles.footerNote} variants={rise}>Thanks for stopping by - I&apos;m always happy to connect.</motion.p>
  </motion.main>;
}
